import Store from 'Store/.';
import { Dispatch } from 'redux';
import Api from 'Helpers/Api';

import {
  ManualSeatPickerAction,
  setIsFetchingSections,
  setFetchSectionsFailed,
  setFetchInnerSectionFailed,
  setFetchCurrentReservationsFailed,
  setNewReservationFailed,
  setSectionOverview,
  setInnerSection,
  setCurrentReservations,
  setSelectedSeats,
  increaseReservationsOnFetch,
  decreaseReservationOnFetch,
  setIsFetchingReservations,
} from './actions';

export const fetchSectionOverview = (performanceID: number) => {
  return async (dispatch: Dispatch): Promise<void> => {
    const { isFetchingSections } = Store.getState().manualSeatPicker;

    if (isFetchingSections || !performanceID) {
      return null;

    }

    dispatch(setIsFetchingSections());
    
    try {
      const response = await Api.get('VenueSections', `performanceID=${ performanceID }`);
      const sectionOverview: Julius.Website.SectionsOverviewModel = await response.json();

      if (response.status === 200) {
        dispatch(setSectionOverview(sectionOverview));
      } else {
        dispatch(setFetchSectionsFailed());
      }
    } catch (e) {
      dispatch(setFetchSectionsFailed());
    }
  };
};

export const fetchInnerSection = (performanceID: number, innerSectionID: number) => {
  return async (dispatch: Dispatch): Promise<void> => {
    if (!performanceID || !innerSectionID) {
      return null;
    }

    try {
      const queryParams = `performanceID=${ performanceID }&innerSectionID=${ innerSectionID }`;
      const response = await Api.get('VenueInnerSection', queryParams);
      const innerSection: Julius.Website.InnerSectionViewModel = await response.json();

      if (response.status === 200) {
        dispatch(setInnerSection(innerSection));
      } else {
        dispatch(setFetchInnerSectionFailed());
      }
    }
    catch (e) {
      dispatch(setFetchInnerSectionFailed());
    }
  };
};

export const fetchNewReservation = (seat: Julius.Website.SectionSeatViewModel) => {
  return async (dispatch: Dispatch<ManualSeatPickerAction>): Promise<void> => {
    dispatch(increaseReservationsOnFetch(1));

    try {
      const queryParams = `performanceID=${ seat.performanceID }&seatID=${ seat.seatID }`;
      const response = await Api.get('ReservateSeat', queryParams);
      const isSuccess = await response.json();
      dispatch(decreaseReservationOnFetch(1));

      if (response.status !== 200 || !isSuccess) {
        dispatch(setNewReservationFailed());
      }
    }
    catch (e) {
      dispatch(setNewReservationFailed());
      dispatch(decreaseReservationOnFetch(1));
    }
  };
};

export const fetchNewReservations = (seats: Julius.Website.SectionSeatViewModel[]) => {
  return async (dispatch: Dispatch<ManualSeatPickerAction>): Promise<void> => {
    dispatch(increaseReservationsOnFetch(1));
    dispatch(setIsFetchingReservations(true));

    try {
      const seatIDs = seats.map(seat => seat.seatID);
      const { performanceID } = seats[0];
      const queryParams = `performanceID=${ performanceID }&seatIDs=${ JSON.stringify(seatIDs) }`;
      const response = await Api.get('ReservateSeats', queryParams);
      const isSuccess = await response.json();
      dispatch(decreaseReservationOnFetch(1));

      if (response.status !== 200 || !isSuccess) {
        dispatch(setNewReservationFailed());
      }
    }
    catch (e) {
      dispatch(setNewReservationFailed());
      dispatch(decreaseReservationOnFetch(1));
    } finally {
      dispatch(setIsFetchingReservations(false));
    }
  };
};

export const fetchNewReservationsNonNumbered = (performanceID: number, innerSectionId: number, sectionId: number, priceCategoryId: number, discountId: number, numberOfSeats: number) => {
  return async (dispatch: Dispatch<ManualSeatPickerAction>): Promise<void> => {
    dispatch(increaseReservationsOnFetch(numberOfSeats));
    dispatch(setIsFetchingReservations(true));

    try {
      const queryParams = `performanceID=${ performanceID }&innerSectionID=${ innerSectionId }&sectionID=${ sectionId }&priceCategoryId=${ priceCategoryId }&discountId=${ discountId }&numberOfSeats=${ numberOfSeats }`;
      const response = await Api.get('ReservateNonNumberedSeats', queryParams);
      const isSuccess = await response.json();
      dispatch(decreaseReservationOnFetch(numberOfSeats));

      if (response.status !== 200 || !isSuccess) {
        dispatch(setNewReservationFailed());
      }
    }
    catch (e) {
      dispatch(setNewReservationFailed());
      dispatch(decreaseReservationOnFetch(numberOfSeats));
    } finally {
      dispatch(setIsFetchingReservations(false));
    }
  };
};

export const deleteSeatReservation = (seats: Julius.Website.OrderSeatViewModel[]) => {
  return async (dispatch: Dispatch): Promise<void> => {
    dispatch(increaseReservationsOnFetch(1));
    try {
      const seatIDs = seats.map(seat => seat.seatID);
     
      const queryParams = `seatIDs=${ JSON.stringify(seatIDs)}`;
      const response = await Api.get('RemoveSeats',`${ queryParams}`);
      
      const isSuccess = await response.json();
      dispatch(decreaseReservationOnFetch(1));
    }
    catch (e) {
      dispatch(decreaseReservationOnFetch(1));
    }
  };
};

export const fetchCurrentReservations = () => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await Api.get('CurrentManualReservations');
      const result: Julius.Website.ManualReservationViewModel = await response.json();

      if (response.status === 200 && result) {
        dispatch(setCurrentReservations(result));
      } else {
        dispatch(setFetchCurrentReservationsFailed());
      }
    }
    catch (e) {
      dispatch(setFetchCurrentReservationsFailed());
    }
  };
};

export const updateSeatDiscount = (newSeat: Julius.Website.OrderSeatViewModel) => {
  return async (dispatch: Dispatch): Promise<void> => {
    const { selectedSeats } = Store.getState().manualSeatPicker;

    try {
      const queryParams = `performanceID=${ newSeat.performanceID }&seatID=${ newSeat.seatID }&reservationID=${ newSeat.reservationID }&discountID=${ newSeat.discountID }`;
      const response = await Api.get('ChangeDiscount', queryParams);
      const isSuccess = await response.json();

      if (isSuccess) {
        const index = selectedSeats.findIndex(s => s.seatID === newSeat.seatID);
        const newSelectedSeats = selectedSeats.slice(0, index).concat(newSeat).concat(selectedSeats.slice(index + 1));
        dispatch(setSelectedSeats(newSelectedSeats));
      }
    } catch (e) {
      dispatch(setSelectedSeats(selectedSeats));
    }
  };
};


// WEBPACK FOOTER //
// ./src/components/PickSeatManually/Redux/thunks.tsx